import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Upload, Eye, Share2, Trash2, Loader2, BarChart3, Store } from "lucide-react";
import { Card, CardContent } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../components/ui/dialog";
import MarketplaceView from "./marketplace/MarketplaceView";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const ReportingTab = ({ token, requestDelete, userRole, isSuperAdmin, canViewCampaigns = false, canEditCampaigns = false, canViewMarketplace = false }) => {
  // Resolve effective capabilities. Super Admin keeps full access for backward compat.
  const showCampaigns = isSuperAdmin || canViewCampaigns;
  const showMarketplace = isSuperAdmin || canViewMarketplace;
  const editCampaigns = isSuperAdmin || canEditCampaigns;

  const [view, setView] = useState(showCampaigns ? "campaigns" : "marketplace");

  // If permissions change at runtime (rare), keep the view valid.
  useEffect(() => {
    if (view === "campaigns" && !showCampaigns) setView("marketplace");
    if (view === "marketplace" && !showMarketplace) setView("campaigns");
  }, [showCampaigns, showMarketplace, view]);

  return (
    <>
      {/* Header with View Toggle */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-8">
        <div>
          <h2 className="text-2xl font-bold text-white font-mono" data-testid="reporting-heading">Reporting</h2>
          <p className="text-[#ebebeb]/60 font-mono text-sm mt-1">
            {view === "campaigns" ? "Upload and manage DSP campaign reports" : "Multi-source SSP marketplace analytics"}
          </p>
        </div>
        {/* Toggle — show only when BOTH sub-views are accessible */}
        {showCampaigns && showMarketplace ? (
          <div className="flex items-center bg-[#1e2124] rounded-xl border border-[#2a2d31] p-1" data-testid="reporting-view-toggle">
            <button
              onClick={() => setView("campaigns")}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-mono transition-all ${
                view === "campaigns"
                  ? "bg-[#ff3737]/15 text-[#ff3737] shadow-sm"
                  : "text-[#ebebeb]/50 hover:text-[#ebebeb]/80"
              }`}
              data-testid="toggle-campaigns"
            >
              <BarChart3 className="w-4 h-4" /> Campaigns
            </button>
            <button
              onClick={() => setView("marketplace")}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-mono transition-all ${
                view === "marketplace"
                  ? "bg-[#ff3737]/15 text-[#ff3737] shadow-sm"
                  : "text-[#ebebeb]/50 hover:text-[#ebebeb]/80"
              }`}
              data-testid="toggle-marketplace"
            >
              <Store className="w-4 h-4" /> Marketplace
            </button>
          </div>
        ) : null}
      </div>

      {view === "campaigns" && showCampaigns ? (
        <CampaignsView token={token} requestDelete={requestDelete} canEdit={editCampaigns} />
      ) : (
        <MarketplaceView token={token} requestDelete={requestDelete} isSuperAdmin={isSuperAdmin} />
      )}
    </>
  );
};

/* Original Campaigns view extracted as sub-component */
const CampaignsView = ({ token, requestDelete, canEdit = true }) => {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [campaignsLoading, setCampaignsLoading] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadCpm, setUploadCpm] = useState("80");
  const [uploadCurrency, setUploadCurrency] = useState("HKD");
  const [uploading, setUploading] = useState(false);

  const fetchCampaigns = useCallback(async () => {
    setCampaignsLoading(true);
    try {
      const response = await axios.get(`${API}/campaigns`, { headers: { Authorization: `Bearer ${token}` } });
      setCampaigns(response.data);
    } catch { toast.error("Failed to fetch campaigns"); }
    finally { setCampaignsLoading(false); }
  }, [token]);

  useEffect(() => { fetchCampaigns(); }, [fetchCampaigns]);

  const handleUpload = async () => {
    if (!uploadFile) { toast.error("Select a file"); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", uploadFile);
      formData.append("cpm", uploadCpm);
      formData.append("currency", uploadCurrency);
      const res = await axios.post(`${API}/campaigns/upload`, formData, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" }
      });
      toast.success("Campaign uploaded");
      setShowUpload(false); setUploadFile(null);
      fetchCampaigns();
      if (res.data?.id) navigate(`/campaigns/${res.data.id}`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); }
  };

  const handleDeleteCampaign = (id, name) => {
    requestDelete("campaign", id, name, async () => {
      try {
        await axios.delete(`${API}/campaigns/${id}`, { headers: { Authorization: `Bearer ${token}` } });
        setCampaigns(prev => prev.filter(c => c.id !== id));
        toast.success("Campaign deleted");
      } catch { toast.error("Failed to delete campaign"); }
    });
  };

  return (
    <>
      {canEdit && (
        <div className="flex justify-end mb-4">
          <button onClick={() => setShowUpload(true)} className="ingenium-button" data-testid="upload-campaign-btn">
            <span className="flex items-center gap-2"><Upload className="w-4 h-4" /> Upload Report</span>
          </button>
        </div>
      )}

      <Card className="bg-[#1e2124] border-[#2a2d31]">
        <CardContent className="p-0">
          {campaignsLoading ? (
            <div className="flex items-center justify-center p-12"><Loader2 className="w-6 h-6 text-[#ff3737] animate-spin" /></div>
          ) : campaigns.length === 0 ? (
            <div className="text-center p-12">
              <BarChart3 className="w-12 h-12 text-[#ebebeb]/30 mx-auto mb-3" />
              <p className="text-[#ebebeb]/60 font-mono">No campaigns uploaded yet</p>
              <p className="text-[#ebebeb]/40 font-mono text-sm mt-1">Upload an Excel or CSV report to get started</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-[#2a2d31] hover:bg-transparent">
                  <TableHead className="text-[#ebebeb]/60 font-mono">Campaign</TableHead>
                  <TableHead className="text-[#ebebeb]/60 font-mono">Impressions</TableHead>
                  <TableHead className="text-[#ebebeb]/60 font-mono">Clicks</TableHead>
                  <TableHead className="text-[#ebebeb]/60 font-mono">CTR</TableHead>
                  <TableHead className="text-[#ebebeb]/60 font-mono">Spend</TableHead>
                  <TableHead className="text-[#ebebeb]/60 font-mono">Uploaded</TableHead>
                  <TableHead className="text-[#ebebeb]/60 font-mono text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {campaigns.map((c) => (
                  <TableRow key={c.id} className="border-[#2a2d31] hover:bg-white/5 cursor-pointer" data-testid={`campaign-row-${c.id}`}>
                    <TableCell className="font-mono text-white font-medium">
                      <button onClick={() => navigate(`/campaigns/${c.id}`)} className="text-left hover:text-[#ff3737] transition-colors" data-testid={`campaign-link-${c.id}`}>{c.name}</button>
                    </TableCell>
                    <TableCell className="font-mono text-[#ebebeb]">{(c.summary?.impressions || 0).toLocaleString()}</TableCell>
                    <TableCell className="font-mono text-[#ebebeb]">{(c.summary?.clicks || 0).toLocaleString()}</TableCell>
                    <TableCell className="font-mono text-[#ebebeb]">{(c.summary?.ctr || 0).toFixed(2)}%</TableCell>
                    <TableCell className="font-mono text-[#ebebeb]">{c.currency} {(c.summary?.total_spend || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</TableCell>
                    <TableCell className="font-mono text-[#ebebeb]/60 text-sm">{c.created_at ? new Date(c.created_at).toLocaleDateString() : "-"}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => navigate(`/campaigns/${c.id}`)} className="p-1.5 rounded hover:bg-white/10 text-[#ebebeb]/60 hover:text-white" title="View"><Eye className="w-4 h-4" /></button>
                        <button onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/campaigns/view?share=${c.share_token}`); toast.success("Share link copied"); }} className="p-1.5 rounded hover:bg-white/10 text-[#ebebeb]/60 hover:text-white" title="Share"><Share2 className="w-4 h-4" /></button>
                        {canEdit && (
                          <button onClick={() => handleDeleteCampaign(c.id, c.name)} className="p-1.5 rounded hover:bg-red-500/20 text-[#ebebeb]/60 hover:text-red-400" title="Delete"><Trash2 className="w-4 h-4" /></button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Upload Modal */}
      <Dialog open={showUpload} onOpenChange={setShowUpload}>
        <DialogContent className="bg-[#191b1d] border-white/10 text-white">
          <DialogHeader><DialogTitle className="font-mono">Upload Campaign Report</DialogTitle><DialogDescription className="text-[#ebebeb]/50">Upload an Excel or CSV file</DialogDescription></DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <label className="text-[#ebebeb]/60 text-sm font-mono mb-1 block">Report File</label>
              <input type="file" accept=".xlsx,.xls,.csv" onChange={(e) => setUploadFile(e.target.files?.[0])} className="form-input w-full text-sm" data-testid="upload-file-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-[#ebebeb]/60 text-sm font-mono mb-1 block">CPM</label>
                <Input type="number" value={uploadCpm} onChange={(e) => setUploadCpm(e.target.value)} className="form-input" data-testid="upload-cpm" />
              </div>
              <div>
                <label className="text-[#ebebeb]/60 text-sm font-mono mb-1 block">Currency</label>
                <select value={uploadCurrency} onChange={(e) => setUploadCurrency(e.target.value)} className="form-input w-full" data-testid="upload-currency">
                  <option value="HKD">HKD</option>
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                  <option value="GBP">GBP</option>
                  <option value="SGD">SGD</option>
                </select>
              </div>
            </div>
          </div>
          <DialogFooter className="mt-4">
            <button onClick={() => setShowUpload(false)} className="px-4 py-2 rounded-lg border border-white/10 text-[#ebebeb]/60">Cancel</button>
            <button onClick={handleUpload} disabled={uploading || !uploadFile} className="ingenium-button" data-testid="upload-submit-btn">
              {uploading ? <><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Uploading...</> : "Upload"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ReportingTab;
